import React, { useCallback, useEffect, useState } from "react";
import { Stack, Typography, Box, Alert, Button, Chip, Divider } from "@mui/material";

import { Surface, SectionTitle } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

// How each recorded action should read to a person scanning the timeline.
const ACTION_META = {
  "profile.update": { label: "Profile edited", tone: T.info },
  "bank.update": { label: "Bank details changed", tone: T.warning },
  "bank.reveal": { label: "Bank details viewed", tone: T.muted },
  "verification.update": { label: "Approval changed", tone: T.primary },
  "takingOrders.update": { label: "Order-taking changed", tone: T.primary },
  "loginStatus.update": { label: "Forced offline", tone: T.warning },
  "partner.delete": { label: "Partner deleted", tone: T.error },
};

const meta = (action) => ACTION_META[action] || { label: action, tone: T.muted };

const when = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

// Values in `before`/`after` are whatever the endpoint recorded — a scalar for
// a field edit, an object for a bank change.
function renderValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${k}: ${v}`)
      .join(" · ");
  }
  return String(value);
}

function Entry({ row }) {
  const { label, tone } = meta(row.action);
  const who = row.actor?.email || row.actor?.name || row.actor?.id || "Unknown admin";
  const showsChange = row.before !== null || row.after !== null;

  return (
    <Box sx={{ display: "flex", gap: 1.5, py: 1.6 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: tone,
          mt: 0.8,
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: T.ink }}>
            {label}
          </Typography>
          {row.entity && (
            <Chip
              size="small"
              label={row.entity}
              sx={{
                fontSize: "0.68rem",
                height: 20,
                bgcolor: tint(tone, 0.1),
                color: tone,
              }}
            />
          )}
          <Typography sx={{ fontSize: "0.72rem", color: T.subtle, ml: "auto" }}>
            {when(row.at)}
          </Typography>
        </Stack>

        {showsChange && (
          <Typography sx={{ fontSize: "0.8rem", color: T.muted, mt: 0.4 }}>
            {renderValue(row.before)} → <b>{renderValue(row.after)}</b>
          </Typography>
        )}

        <Typography sx={{ fontSize: "0.75rem", color: T.subtle, mt: 0.4 }}>
          {who}
          {row.reason ? ` · “${row.reason}”` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Everything an admin has done to this partner.
 *
 * The first time someone asks why a partner stopped getting work, or who
 * changed their bank account, this is the only thing that can answer it.
 */
export default function ActivityTab({ partnerId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (nextPage) => {
      setLoading(true);
      setError("");
      try {
        const data = await api.getActivity(partnerId, { page: nextPage, limit: 25 });
        setRows((prev) =>
          nextPage === 1 ? data.activity || [] : [...prev, ...(data.activity || [])]
        );
        setTotal(data?.pagination?.total || 0);
        setPage(nextPage);
      } catch (err) {
        setError(err?.response?.data?.message || "Could not load the activity log.");
      } finally {
        setLoading(false);
      }
    },
    [partnerId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <Surface sx={{ p: { xs: 2, md: 2.5 } }}>
      <SectionTitle>
        Activity{total ? ` · ${total} entr${total === 1 ? "y" : "ies"}` : ""}
      </SectionTitle>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !rows.length && !error && (
        <Typography sx={{ fontSize: "0.85rem", color: T.subtle, py: 4, textAlign: "center" }}>
          Nothing has been changed on this partner yet.
        </Typography>
      )}

      <Stack divider={<Divider />}>
        {rows.map((row) => (
          <Entry key={row._id} row={row} />
        ))}
      </Stack>

      {rows.length < total && (
        <Stack alignItems="center" sx={{ pt: 2 }}>
          <Button
            size="small"
            onClick={() => load(page + 1)}
            disabled={loading}
            sx={{ textTransform: "none", color: T.primary }}
          >
            {loading ? "Loading…" : `Load older (${total - rows.length} more)`}
          </Button>
        </Stack>
      )}
    </Surface>
  );
}
